import {createStyles, Table, Tooltip} from '@mantine/core';
import React from 'react';

import ColumnDropdownSelector from 'data-frames/column-selector/ColumnDropdownSelector';
import MultiSelectColumnDropdown from 'data-frames/column-selector/MultiSelectColumnDropdown';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {LeafType, LeafTypeToFriendlyName} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {LEAF_TYPE_TO_ICON} from 'data-types/benchling-type-def/leafTypeToIcon';
import {FIELD_TYPE_TO_LEAF_TYPE, FIELD_TYPES_BY_TYPE_STRING} from 'schemas/fields/SchemaFieldConstants';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

import {ConfigShapePath} from '../../StepConfigEditorTypes';
import {HelpButtonOrNull} from '../SectionName';

export interface ColumnMappingRow {
  id: string;
  displayName: string;
  targetPath: ConfigShapePath;
  required: boolean;
  multiSelect: boolean;
  fieldType?: string | null;
  allowedLeafTypes?: ReadonlyArray<LeafType> | null;
  infoText?: string;
}

interface FieldTypeDisplay {
  icon: BenchlingIconName;
  label: string;
}

const FIELD_TYPE_TO_LEAF_TYPE_BY_STRING: Record<string, LeafType | undefined> = FIELD_TYPE_TO_LEAF_TYPE;

function getFieldTypeDisplay(fieldType: string | null | undefined): FieldTypeDisplay | null {
  if (fieldType == null) {
    return null;
  }
  const leafType = FIELD_TYPE_TO_LEAF_TYPE_BY_STRING[fieldType];
  if (leafType == null) {
    return null;
  }
  return {
    icon: LEAF_TYPE_TO_ICON[leafType],
    label: FIELD_TYPES_BY_TYPE_STRING[fieldType]?.label ?? fieldType,
  };
}

function getLeafTypeDisplay(
  allowedLeafTypes: ReadonlyArray<LeafType> | null | undefined
): FieldTypeDisplay | null {
  if (allowedLeafTypes == null || allowedLeafTypes.length === 0) {
    return null;
  }
  // When multiple types are allowed (e.g. Integer or Decimal) they may share an icon, so show the first
  // type's icon and list every type's name.
  return {
    icon: LEAF_TYPE_TO_ICON[allowedLeafTypes[0]],
    label: allowedLeafTypes.map((leafType) => LeafTypeToFriendlyName[leafType]).join(' or '),
  };
}

interface ColumnMappingGridProps {
  rows: Array<ColumnMappingRow>;
  dataFrameSpec: Pick<DataFrameSpec, 'columns'>;
  valueForPath: (path: ConfigShapePath) => JSONValue | null;
  onChange: (path: ConfigShapePath, value: JSONValue) => void;
  disabled?: boolean;
}

const useStyles = createStyles((theme) => ({
  table: {
    tableLayout: 'fixed',
    width: '100%',
  },
  fieldNameCell: {
    width: '40%',
  },
  fieldNameContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  typeIcon: {
    display: 'flex',
    color: theme.colors.gray[6],
    flexShrink: 0,
  },
  requiredAsterisk: {
    color: theme.colors.red[8],
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
  columnCell: {
    width: '60%',
  },
}));

function asStringOrNull(value: JSONValue | null): string | null {
  return typeof value === 'string' ? value : null;
}

function asStringArray(value: JSONValue | null): Array<string> {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

export default function ColumnMappingGrid({
  rows,
  dataFrameSpec,
  valueForPath,
  onChange,
  disabled,
}: ColumnMappingGridProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <Table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.fieldNameCell}>Field</th>
          <th className={classes.columnCell}>Column</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const typeDisplay = getFieldTypeDisplay(row.fieldType) ?? getLeafTypeDisplay(row.allowedLeafTypes);
          return (
            <tr key={row.id}>
              <td className={classes.fieldNameCell}>
                <div className={classes.fieldNameContent}>
                  {typeDisplay != null && (
                    <Tooltip label={typeDisplay.label} withArrow={true}>
                      <span className={classes.typeIcon}>
                        <BenchlingIcon icon={typeDisplay.icon} altText={typeDisplay.label} />
                      </span>
                    </Tooltip>
                  )}
                  {row.displayName}
                  {row.required && (
                    <>
                      <span className={classes.requiredAsterisk} aria-hidden={true}>
                        {' '}
                        *
                      </span>
                      <span className={classes.visuallyHidden}> (required)</span>
                    </>
                  )}
                  <HelpButtonOrNull infoText={row.infoText} isCompact={true} />
                </div>
              </td>
              <td className={classes.columnCell}>
                {row.multiSelect ? (
                  <MultiSelectColumnDropdown
                    dataFrameSpec={dataFrameSpec}
                    validationOptions={{allowedLeafTypes: row.allowedLeafTypes ?? undefined}}
                    selectedColumnSystemNames={asStringArray(valueForPath(row.targetPath))}
                    onChange={(columnSystemNames) => {
                      onChange(row.targetPath, columnSystemNames);
                    }}
                    noColumnSelectedText="Select columns"
                    disabled={disabled}
                  />
                ) : (
                  <ColumnDropdownSelector
                    dataFrameSpec={dataFrameSpec}
                    validationOptions={{allowedLeafTypes: row.allowedLeafTypes ?? undefined}}
                    selectedColumnSystemName={asStringOrNull(valueForPath(row.targetPath))}
                    onChange={(columnSystemName) => {
                      onChange(row.targetPath, columnSystemName);
                    }}
                    noColumnSelectedText="Select a column..."
                    allowSelectNull={true}
                    disabled={disabled}
                    displayMissingColumnName={true}
                  />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
